import { Component, ErrorInfo, ReactNode } from 'react';
import { logger } from '../../lib/logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });

    // Log l'erreur pour le debugging
    logger.error('ErrorBoundary caught an error:', error, errorInfo);

    // TODO: Envoyer l'erreur à un service de monitoring (Sentry, etc.)
    // if (import.meta.env.PROD) {
    //   sendErrorToMonitoring(error, errorInfo);
    // }
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleGoHome = (): void => {
    window.location.href = '/';
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-cream-50 px-4">
          <div className="w-full max-w-md text-center">
            {/* Icône d'erreur */}
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-red-100">
              <svg
                className="h-10 w-10 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>

            {/* Message principal */}
            <h1 className="mb-2 text-2xl font-bold text-leather-900">
              Oups ! Une erreur est survenue
            </h1>
            <p className="mb-6 text-leather-600">
              Nous sommes désolés, quelque chose s'est mal passé.
              Veuillez réessayer ou retourner à l'accueil.
            </p>

            {/* Détails de l'erreur en mode dev */}
            {import.meta.env.DEV && this.state.error && (
              <div className="mb-6 rounded-lg bg-red-50 p-4 text-left">
                <p className="mb-2 font-mono text-sm font-semibold text-red-800">
                  {this.state.error.name}: {this.state.error.message}
                </p>
                {this.state.errorInfo && (
                  <pre className="max-h-40 overflow-auto text-xs text-red-700">
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </div>
            )}

            {/* Boutons d'action */}
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
              <button
                onClick={this.handleReset}
                className="rounded-lg bg-leather-800 px-6 py-3 font-medium text-white transition-colors hover:bg-leather-900"
              >
                Réessayer
              </button>
              <button
                onClick={this.handleGoHome}
                className="rounded-lg border border-leather-300 bg-white px-6 py-3 font-medium text-leather-800 transition-colors hover:bg-leather-50"
              >
                Retour à l'accueil
              </button>
            </div>

            {/* Lien contact */}
            <p className="mt-8 text-sm text-leather-500">
              Si le problème persiste,{' '}
              <a
                href="/contact"
                className="text-gold-600 underline hover:text-gold-700"
              >
                contactez-nous
              </a>
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
